import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@ngrx/store';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, StoreModule } from '@ngrx/store';
import { filter, map, take, switchMap, tap, catchError, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as i1$2 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i1$1 from '@angular/common/http';
import { COMMON_APP_CONFIG } from '@rogers/self-serve-common';
const loadAppSettings = createAction('[App Setting] Load App Settings', props());
const loadAppSettingsSuccess = createAction('[App Setting] Load App Settings Success', props());
const loadAppSettingsFailure = createAction('[App Setting] Load App Settings Failure', props());
const APP_SETTINGS_FEATURE_KEY = 'appSettings';
const initialState = {
  settings: [],
  loading: false,
  loaded: false,
  hasError: false,
  error: null
};
const reducer = createReducer(initialState, on(loadAppSettings, state => ({
  ...state,
  loading: true,
  hasError: false,
  error: null
})), on(loadAppSettingsSuccess, (state, {
  settings
}) => ({
  ...state,
  settings,
  loading: false,
  loaded: true,
  hasError: false,
  error: null
})), on(loadAppSettingsFailure, (state, {
  error
}) => ({
  ...state,
  loading: false,
  loaded: false,
  hasError: true,
  error
})));
const appSettingReducer = (state, action) => {
  return reducer(state, action);
};
const selectAppSettingState = createFeatureSelector(APP_SETTINGS_FEATURE_KEY);
const selectSettings = createSelector(selectAppSettingState, state => state.settings);
const selectSettingsLoaded = createSelector(selectAppSettingState, state => state.loaded);
const selectSettingsLoading = createSelector(selectAppSettingState, state => state.loading);
const selectSettingsHasError = createSelector(selectAppSettingState, state => state.hasError);
let AppSettingFacadeService = /*#__PURE__*/(() => {
  class AppSettingFacadeService {
    constructor(store) {
      this.store = store;
    }
    getSettings$() {
      return this.store.select(selectAppSettingState).pipe(filter(appSettingState => appSettingState.loaded || appSettingState.hasError), map(appSettingState => appSettingState.loaded ? appSettingState.settings : []));
    }
    loadSettingsIfNotLoaded(application) {
      this.store.select(selectSettingsLoaded).pipe(take(1), switchMap(loaded => !loaded ? this.store.select(selectSettingsLoading).pipe(take(1), tap(loading => {
        if (!loading) {
          this.store.dispatch(loadAppSettings({
            application
          }));
        }
      })) : of(loaded))).subscribe();
    }
    getSettingForContext$(context = '') {
      return this.getSettings$().pipe(map(settings => settings.find(setting => setting.context === context)?.settings || []));
    }
    getSettingForContextAndKey$(key, context = '') {
      return this.getSettingForContext$(context).pipe(map(settings => settings.find(setting => setting.key === key)?.value || ''));
    }
    static {
      this.ɵfac = function AppSettingFacadeService_Factory(t) {
        return new (t || AppSettingFacadeService)(i0.ɵɵinject(i1.Store));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AppSettingFacadeService,
        factory: AppSettingFacadeService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AppSettingFacadeService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AppSettingService = /*#__PURE__*/(() => {
  class AppSettingService {
    constructor(appConfig, httpClient) {
      this.appConfig = appConfig;
      this.httpClient = httpClient;
    }
    getAppSettings$(application) {
      const url = `${this.appConfig.contentApiUrlUnauthenticated}settings/${application}`;
      return this.httpClient.get(url, {
        headers: this.appConfig.getHeaders()
      }).pipe(map(response => response?.content ?? []), catchError(() => of([])));
    }
    static {
      this.ɵfac = function AppSettingService_Factory(t) {
        return new (t || AppSettingService)(i0.ɵɵinject(COMMON_APP_CONFIG), i0.ɵɵinject(i1$1.HttpClient));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AppSettingService,
        factory: AppSettingService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AppSettingService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AppSettingEffects = /*#__PURE__*/(() => {
  class AppSettingEffects {
    constructor(appConfig, actions$, appSettingService) {
      this.appConfig = appConfig;
      this.actions$ = actions$;
      this.appSettingService = appSettingService;
      this.loadAppSettings$ = createEffect(() => this.actions$.pipe(ofType(loadAppSettings), mergeMap(({
        application
      }) => this.appSettingService.getAppSettings$(application).pipe(map(settings => loadAppSettingsSuccess({
        settings
      })), catchError(error => of(loadAppSettingsFailure({
        error: this.appConfig.getErrorTrackingObject(error)
      })))))));
    }
    static {
      this.ɵfac = function AppSettingEffects_Factory(t) {
        return new (t || AppSettingEffects)(i0.ɵɵinject(COMMON_APP_CONFIG), i0.ɵɵinject(i1$2.Actions), i0.ɵɵinject(AppSettingService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AppSettingEffects,
        factory: AppSettingEffects.ɵfac
      });
    }
  }
  return AppSettingEffects;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AppSettingStoreModule = /*#__PURE__*/(() => {
  class AppSettingStoreModule {
    static {
      this.ɵfac = function AppSettingStoreModule_Factory(t) {
        return new (t || AppSettingStoreModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AppSettingStoreModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [StoreModule.forFeature(APP_SETTINGS_FEATURE_KEY, appSettingReducer), EffectsModule.forFeature([AppSettingEffects])]
      });
    }
  }
  return AppSettingStoreModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { AppSettingFacadeService, AppSettingStoreModule };
